import type { SearchGeoGroupInput, SearchGeoGroupItem } from './partnerships-history-filters';

interface RawSearch {
  search_id?: string;
  status?: string;
  created_at?: string;
  properties_retrieved?: number;
  agencies_found?: number;
  sources_count?: number;
  agencies_count?: number;
  city?: string;
  neighbourhood?: string;
  query?: Record<string, unknown>;
}

interface RawGroup {
  geo_key?: string;
  city?: string;
  neighbourhood?: string;
  country?: string;
  searches?: RawSearch[];
}

/**
 * Maps a raw backend search into the frontend item, filling geo defaults from its group.
 */
function mapSearch(raw: RawSearch, groupCity: string, groupNeighbourhood?: string): SearchGeoGroupItem {
  const query = (raw.query ?? {}) as Record<string, string>;
  return {
    search_id: raw.search_id ?? '',
    status: (raw.status ?? 'pending') as SearchGeoGroupItem['status'],
    created_at: raw.created_at ?? '',
    properties_retrieved: raw.properties_retrieved ?? 0,
    sources_count: raw.sources_count ?? 0,
    agencies_count: raw.agencies_count ?? 0,
    city: raw.city ?? query.city ?? groupCity,
    neighbourhood: raw.neighbourhood ?? query.neighbourhood ?? groupNeighbourhood,
  };
}

/**
 * Maps a raw backend group into the frontend geo-group, deriving city/neighbourhood from its first search when absent.
 */
function mapGroup(raw: RawGroup): SearchGeoGroupInput {
  const searches = Array.isArray(raw.searches) ? raw.searches : [];
  const firstQuery = (searches[0]?.query ?? {}) as Record<string, string>;
  const city = [raw.city, firstQuery.city].find(Boolean) ?? '';
  const neighbourhood = [raw.neighbourhood, firstQuery.neighbourhood].find(Boolean);

  return {
    geo_key: raw.geo_key ?? '',
    city,
    neighbourhood,
    searches: searches.map((s) => mapSearch(s, city, neighbourhood)),
  };
}

/**
 * Parses the backend `GET /partnerships/searches` response into the frontend
 * geo-group model. The backend returns `{ groups: [...] }` where each group
 * has `geo_key`, optional `city`/`neighbourhood`/`country`, and `searches[]`.
 *
 * Returns `[]` for any malformed, missing, or legacy-shaped input.
 */
export function parseSearchGroups(resp: unknown): SearchGeoGroupInput[] {
  if (!resp || typeof resp !== 'object') return [];
  const groups = (resp as Record<string, unknown>).groups;
  if (!Array.isArray(groups)) return [];
  return groups.map((raw: RawGroup) => mapGroup(raw));
}
